import React, { useState, useEffect } from 'react';
import { apiClient } from '../api';
import { ShieldAlert, AlertTriangle, AlertCircle, Info, Search, RefreshCw, ExternalLink } from 'lucide-react';

const SEVERITY_COLORS = {
  CRITICAL: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20', icon: ShieldAlert },
  HIGH: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/20', icon: AlertTriangle },
  MEDIUM: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', icon: AlertCircle },
  LOW: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', icon: Info },
};

const Vulnerabilities = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const params = severityFilter !== 'all' ? `?severity=${severityFilter}` : '';
      const res = await apiClient.get(`/api/v2/cve/alerts${params}`);
      setAlerts(res.data.alerts || []);
    } catch {
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAlerts(); }, [severityFilter]);

  const filtered = alerts.filter(a =>
    !searchTerm ||
    a.cve_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.asset_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (a.summary && a.summary.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const criticalCount = alerts.filter(a => a.severity === 'CRITICAL').length;
  const highCount = alerts.filter(a => a.severity === 'HIGH').length;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Vulnerability Alerts</h1>
        <button onClick={fetchAlerts} className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-300 transition-colors">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
          <div className="text-3xl font-bold text-red-400">{criticalCount}</div>
          <div className="text-sm text-red-400/70">Critical CVEs</div>
        </div>
        <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-4">
          <div className="text-3xl font-bold text-orange-400">{highCount}</div>
          <div className="text-sm text-orange-400/70">High CVEs</div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <div className="text-3xl font-bold text-white">{alerts.length}</div>
          <div className="text-sm text-gray-400">Total Vulnerabilities</div>
        </div>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search CVEs, assets, or descriptions..."
            className="w-full bg-gray-950 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
          />
        </div>
        <select
          value={severityFilter}
          onChange={e => setSeverityFilter(e.target.value)}
          className="px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-sm text-gray-300 focus:outline-none focus:border-cyan-500"
        >
          <option value="all">All Severities</option>
          <option value="CRITICAL">Critical</option>
          <option value="HIGH">High</option>
          <option value="MEDIUM">Medium</option>
          <option value="LOW">Low</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading vulnerabilities...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No vulnerabilities found</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((alert, i) => {
            const sev = SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.LOW;
            const Icon = sev.icon;
            return (
              <div key={i} className={`bg-gray-900 border ${sev.border} rounded-xl p-4 hover:bg-gray-900/80 transition-colors`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${sev.bg}`}>
                      <Icon className={`w-5 h-5 ${sev.text}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${sev.bg} ${sev.text} border ${sev.border}`}>
                          {alert.severity}
                        </span>
                        <span className="text-sm font-mono text-cyan-400">{alert.cve_id}</span>
                        {alert.cvss_score && (
                          <span className="text-xs text-gray-500">CVSS: {alert.cvss_score}</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-400 mt-1">{alert.summary || 'No description available'}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span>Asset: <span className="text-gray-300">{alert.asset_name}</span></span>
                        <span>Service: <span className="text-gray-300">{alert.service_name} {alert.service_version && `v${alert.service_version}`}</span></span>
                        {alert.checked_at && <span>Checked: {new Date(alert.checked_at).toLocaleDateString()}</span>}
                      </div>
                    </div>
                  </div>
                  <a
                    href={`https://nvd.nist.gov/vuln/detail/${alert.cve_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-cyan-400 transition-colors"
                  >
                    <ExternalLink className="w-3 h-3" /> NVD
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Vulnerabilities;
